// Shared canvas-page scaffold — the chrome every pan/zoom canvas surface
// (/studio, /team-and-agents, /tickets, /files …) was repeating inline:
// the dots layer, the left glass sidebar island, and the floating
// right-center create-"+" island with its glass menu. Extracted
// 2026-06-10 per Nika: "make /team and /tickets page canvases similar to
// /studio — reuse in shared whatever is possible for all those canvas
// pages." /studio remains the visual reference; pages extend these by
// bare key and override only what's page-specific.

import { CANVAS_DOTS_DARK, CANVAS_DOTS_LIGHT } from '../functions/canvasBackground.js'

export { CANVAS_DOTS_DARK, CANVAS_DOTS_LIGHT }

// ── Dots layer ──────────────────────────────────────────────────────────
// The 24px radial-gradient grid behind every canvas. Repositioned on each
// panzoom transform tick by initPanzoomBoard's updateDots (which targets
// the page's `Dots` node) — keep the consumer key named `Dots`.
//
// Theme contract (same var-chain pattern as the canvas surfaces): the
// theme-agnostic inline backgroundImage references `--canvas-dots-image`,
// and the per-theme gradient lives as a FLAT custom property in the
// '@dark'/'@light' blocks (compiled to `[data-theme]`-scoped CSS), so the
// grid follows theme flips — OS-driven 'auto' included — with zero JS.
//
// Why a custom property and not a themed backgroundImage (verified live):
// 1. scratch's transformBackgroundImage splits values on ', ' (which hits
//    commas INSIDE gradient parens) and re-joins with spaces — any gradient
//    routed through the flat backgroundImage prop emits invalid CSS that
//    the browser drops (ticketed in tickets/smbls.md). Custom properties
//    bypass CSS_PROPS_REGISTRY transforms entirely.
// 2. A nested `style:{…}` inside '@dark' compiles to a bogus
//    `[data-theme] .cls style` child-element selector — theme-block props
//    must be flat (also ticketed).
// updateDots only stamps backgroundSize/backgroundPosition inline, both
// theme-agnostic.
export const CanvasDots = {
  position: 'absolute',
  inset: '0',
  pointerEvents: 'none',
  opacity: '0.38',
  style: {
    backgroundImage: 'var(--canvas-dots-image)',
    backgroundSize: '24px 24px'
  },
  '@dark': {
    '--canvas-dots-image': CANVAS_DOTS_DARK
  },
  '@light': {
    '--canvas-dots-image': CANVAS_DOTS_LIGHT
  }
}

// ── Left sidebar island ─────────────────────────────────────────────────
// Geometry-only rail (the /studio AppSidebar pattern): floats over the
// canvas at the left edge, inset D1 top/bottom so it clears the floating
// header and bottom toolbars. Pages that render their own per-section
// cards (like /studio) extend THIS; pages that want one glass card extend
// CanvasSidebarCard below.
export const CanvasSidebarIsland = {
  position: 'absolute',
  left: 'X2',
  // --shell-top (= D1 + 3px, defined once in app.js __shell-content-scroll): kept
  // in lockstep with the shell content offset and the CanvasPageSlot top so the
  // rail's top edge never drifts from a page opened in the slot beside it (+3px
  // navbar-gap nudge 2026-06-24). Passed as the BARE custom-property name so
  // scratch wraps it in var() once (a 'var(...)' or calc value gets re-wrapped /
  // hashed into an empty atomic rule). bottom stays D1 — only the top is shared.
  top: '--shell-top',
  bottom: 'D1',
  width: 'G+C',
  minWidth: 'G+C',
  zIndex: '20',
  flow: 'y',
  gap: 'V',
  pointerEvents: 'auto',
  '@mobileL': {
    position: 'fixed',
    left: 'A',
    right: 'A',
    top: '--shell-top',
    bottom: 'auto',
    width: 'auto',
    minWidth: '0',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    maxHeight: '34dvh',
    overflow: 'auto'
  }
}

// Single glass-card variant (the /tickets sidebar / Messenger chat-list
// look, aligned to the studio rail geometry above).
export const CanvasSidebarCard = {
  extends: 'CanvasSidebarIsland',
  theme: 'dialog',
  // Longhand — the theme re-emits border longhands after shorthands.
  borderWidth: '0',
  round: 'radiusCard',
  padding: 'A',
  overflow: 'auto',
  shadow: 'float',
  '@mobileL': {
    padding: 'Z1',
    round: 'radiusCard'
  }
}

// Stronger mobile-only chrome for canvas side panels that sit over dotted
// backgrounds. Kept separate from CanvasSidebarCard so desktop glass pages
// retain the lighter rail treatment.
export const CanvasMobileSidebarPanelChrome = {
  // Border habit removed — the opaque `document` fill + `shadow: 'overlay'`
  // elevation already fully separate this panel from the dotted canvas
  // behind it (design-direction.md §8 — fill contrast, not a stroke).
  background: 'document',
  color: 'title',
  // An opaque sheet in both skins (the skin contract): no backdrop filter.
  // The glass theme a consumer carries on desktop (canvas-card-glass, 35px)
  // would still blur a backdrop this opaque fill covers completely — 'none'
  // switches that cost off. (It was a literal blur(18px) that no pixel showed.)
  backdropFilter: 'none',
  animation: 'none',
  shadow: 'overlay'
}

// ── Create "+" island ───────────────────────────────────────────────────
// One row of the create menu. Per-instance props (flat, v3.14):
//   { label, hint, iconName, onClick }
export const CanvasCreateMenuItem = {
  extends: 'MenuPanelItem',
  attr: { role: 'menuitem' },
  title: (el) => el.label || '',
  flow: 'y',
  align: 'start left',
  gap: '2px',
  // Visible hover so these rows highlight clearly when reused as the cascade
  // menu's 2nd-level flyout — MenuPanelItem's inherited hover (gray6.05) is
  // near-invisible on the dark canvas glass. Matches the cascade's 1st-level
  // row wash (2026-06-14 per Nika "second level of context menu has no hover").
  // Flat `hover` token prop — a :hover block of only @dark/@light sub-blocks
  // emits nothing through css-in-props, and the token pair is byte-identical.
  ':hover': { background: 'hover' },
  Name: {
    tag: 'span',
    display: 'flex',
    flow: 'x',
    gap: 'Y',
    align: 'center left',
    Icon: {
      icon: (el) => el.parent?.parent?.iconName || '',
      fontSize: 'Z2',
      opacity: '0.65',
      ariaHidden: 'true'
    },
    Label: {
      tag: 'span',
      text: (el) => el.parent?.parent?.label || '',
      fontSize: 'Z2',
      fontWeight: '700'
    }
  },
  Hint: {
    tag: 'span',
    text: (el) => el.parent?.hint || '',
    fontSize: 'Z',
    color: 'caption'
  }
}

// Body-only menu — rendered inside the shared DropdownRoot (@symbo.ls/dropdown
// owns placement/flip, outside-click, ESC). Chrome comes from the shared
// MenuPanel (the house dropdown recipe). Pages extend this and add
// CanvasCreateMenuItem rows.
export const CanvasCreateMenuBody = {
  extends: 'MenuPanel',
  minWidth: '220px',
  gap: '2px',
  ariaLabel: (el) => el.call('polyglot', 'editor.canvasCreate.new'),
  '@mobileL': {
    minWidth: '220px',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    maxHeight: '60dvh',
    overflow: 'auto'
  }
}

// The floating right-center "+" island (the /studio StudioNavbar chrome).
// Default AddBtn opens the page's menu via the dropdown plugin, reading
// two flat props off the island:
//   menuId      — dropdown layer id (default 'canvasCreate')
//   menuContent — body-only menu object (usually `{ extends:
//                 'CanvasCreateMenuBody', ...rows }`)
// Pages with bespoke open logic (e.g. captured-root menus) override
// AddBtn.onClick instead.
export const CanvasCreateIsland = {
  position: 'absolute',
  top: '50%',
  right: 'V',
  transform: 'translateY(-50%)',
  zIndex: 30,
  padding: 'V',
  round: 'radiusPill',
  theme: 'dialog',
  borderWidth: '0',
  flow: 'y',
  gap: 'V',
  align: 'center',
  pointerEvents: 'auto',
  shadow: 'float',
  // THE MIRROR (opt-in — functions/pageSidebar.js): the island sits opposite
  // the page's sidebar. A page whose sidebar follows the side rule sets
  // `data-sidebar-side` on its island (`(el, s) => el.call('pageSidebarRead',
  // s.root, el).side`); beside a left navbar the sidebar is on the right, so
  // the island takes the left edge. An island without the attribute stays
  // where it is (a page whose sidebar has not moved yet). The phone reads
  // 'left' (its own dock below holds).
  '&[data-sidebar-side="right"]': { right: 'auto', left: 'V' },
  '@mobileL': {
    position: 'fixed',
    top: 'auto',
    right: 'A',
    // Sit above the composer AND the phone suggestion-chip row (--chip-row,
    // defined in the shell's band system in workspace/app.js). A flat D1 put
    // every canvas FAB — studios, tickets, team, infra — inside the chips'
    // band, and the chips paint over them.
    bottom: 'calc(var(--slot-bottom) + var(--chip-row))',
    transform: 'none',
    flow: 'x'
  },

  AddBtn: {
    tag: 'button',
    type: 'button',
    width: 'controlHCompact',
    height: 'controlHCompact',
    round: '50%',
    border: '0',
    // INVARIANT for consumers: this ink is correct ONLY against the
    // translucent washes declared just below (and against the `overlay`
    // washes CanvasNavbar/StudioNavbar substitute). It is a top-level
    // declared prop, so since smbls f6757537d — which made declared props
    // beat the `theme:` compound — it OUTVOTES any `theme:` an extending
    // consumer sets. A consumer that overrides the fill to a solid ink
    // primary must therefore declare its own `color:` too; `theme: 'primary'`
    // alone will silently keep this `title` and paint ink-on-ink. That is
    // exactly how the tickets board create button reached 1.13:1 with an
    // invisible "+" (fixed workspace cf0323af — see tickets/fable.md).
    // Audited 2026-08-09: board.js was the only consumer overriding the
    // fill; infra, CanvasNavbar, StudioNavbar, team-and-agents and
    // ProjectCanvasSidebar all pair correctly with a wash or set their own
    // ink. Do NOT "fix" this by moving the colour into the @-blocks (same
    // specificity, no effect) or by deleting it (every default island then
    // drops from `title` to the island's dimmer inherited caption ink).
    color: 'title',
    '@dark': {
      background: 'white.08',
      ':hover': { background: 'white.18' }
    },
    '@light': {
      background: 'black.06',
      ':hover': { background: 'black.12' }
    },
    fontSize: 'B',
    fontWeight: '400',
    lineHeight: '1',
    cursor: 'pointer',
    title: (el) => el.call('polyglot', 'common.action.create'),
    ariaLabel: (el) => el.call('polyglot', 'common.action.create'),
    ariaHaspopup: 'true',
    text: '+',
    onClick: (e, el, s) => {
      e.stopPropagation()
      const island = el.parent
      const content = island?.menuContent
      if (!content) return
      el.call('openDropdown', {
        id: island.menuId || 'canvasCreate',
        anchorEl: el,
        placement: 'left-start',
        content
      })
    }
  }
}

// ── Primary "create" FAB variant ────────────────────────────────────────
// The ONE solid-ink create FAB (DESIGN_LANGUAGE §1: primary actions are
// ink, never hue fills) — the look the tickets board converged on. Every
// canvas-page "+" that CREATES something (new project on /studio, new
// page/component on a project canvas, new human/agent on
// /team-and-agents) and the tickets board's create button all extend
// THIS, not the bare CanvasCreateIsland, so the Add affordance is one
// family app-wide (DATE-FMT-1-REMAINDERS-1 — the /studio gray wash and
// the tickets solid FAB had drifted into two looks). Hoisted here from
// canvas/components/CanvasPrimaryCreateIsland.js
// (DESIGN-HOIST-PRIMARY-CREATE-FAB-INTO-SHARED-1) so every shared-library
// consumer — canvas AND tickets — reaches the same definition instead of
// pinning a duplicate token set in lock-step.
//
// Islands that TOGGLE rather than create (sidebar reopen, filters) keep
// the bare CanvasCreateIsland wash — a toggle is not an Add.
export const CanvasPrimaryCreateIsland = {
  extends: 'CanvasCreateIsland',

  AddBtn: {
    // The island's base '+' text glyph is replaced by the DS `plus` icon.
    text: null,
    align: 'center center',
    // Solid-ink primary fill. The island's inherited translucent washes are
    // overridden per theme to the primary fill so the solid-ink look renders.
    // The base island also nests a wash `:hover` INSIDE each @-block, which
    // outvotes the `theme: 'primary'` hover pair — so the hover/active steps
    // are re-declared here from the theme's own auto vars (measured live
    // 2026-08-21: without this, hover painted rgba(0,0,0,.12) under white ink).
    '@dark': {
      background: '--theme-primary-background',
      ':hover': { background: '--theme-primary-hover-background' },
      ':active': { background: '--theme-primary-active-background' }
    },
    '@light': {
      background: '--theme-primary-background',
      ':hover': { background: '--theme-primary-hover-background' },
      ':active': { background: '--theme-primary-active-background' }
    },
    // @coarse touch-target floor; fine pointers unaffected.
    '@coarse': { minWidth: 'C', minHeight: 'C' },
    ':active': { transform: 'scale(0.96)' },
    theme: 'primary',
    // The INK must be declared explicitly — the base CanvasCreateIsland.AddBtn
    // declares a top-level `color: 'title'` that outvotes the theme compound
    // (smbls f6757537d); `theme: 'primary'` alone paints ink-on-ink.
    color: '--theme-primary-color',
    Icon: {
      extends: 'Icon',
      name: 'plus',
      boxSize: 'A',
      pointerEvents: 'none'
    }
  }
}
